// src/components/reporting/usage/UsageUserList.js
// Renders the list of user summary rows at the tenant detail level.
// Each row shows: user name/email, total calls, total cost, per-service call counts.
// Clicking a row is a no-op for now (user detail level is a future phase).

import React from 'react';
import PropTypes from 'prop-types';
import { centsToDollars } from './UsageSummaryPanel';

const SERVICE_LABELS = {
  conversation:    'Conv.',
  analytic_report:   'Analytics',
  ad_hoc_report:     'Ad Hoc',
  assessment_report: 'Assessments',
  stt:               'STT',
  tts:             'TTS',
};

const UsageUserList = ({ userSummaries }) => {
  if (!userSummaries || userSummaries.length === 0) {
    return (
      <div className="usage-user-list usage-empty">
        No users had activity during this period.
      </div>
    );
  }

  return (
    <div className="usage-user-list">
      {/* Table headers */}
      <div className="usage-user-header">
        <div className="uul-name-col">User</div>
        {Object.entries(SERVICE_LABELS).map(([key, label]) => (
          <div key={key} className="uul-service-col">{label}</div>
        ))}
        <div className="uul-calls-col">Total Calls</div>
        <div className="uul-cost-col">Total Cost</div>
      </div>

      {/* User rows */}
      {userSummaries.map((userRow) => {
        const { userId, email, displayName, services, totals } = userRow;
        const nameDisplay = displayName || email;
        const subDisplay  = displayName ? email : null;

        return (
          <div key={userId} className="usage-user-row">
            {/* Identity */}
            <div className="uul-name-col">
              <div className="uul-user-avatar">
                {nameDisplay.charAt(0).toUpperCase()}
              </div>
              <div className="uul-user-info">
                <span className="uul-user-name">{nameDisplay}</span>
                {subDisplay && <span className="uul-user-email">{subDisplay}</span>}
              </div>
            </div>

            {/* Per-service call counts */}
            {Object.keys(SERVICE_LABELS).map((key) => (
              <div key={key} className="uul-service-col">
                <span className="uul-service-value">
                  {services?.[key]?.callCount
                    ? Number(services[key].callCount).toLocaleString()
                    : '—'}
                </span>
              </div>
            ))}

            {/* Totals */}
            <div className="uul-calls-col">
              <span className="uul-total-calls">
                {Number(totals?.totalCallCount ?? 0).toLocaleString()}
              </span>
            </div>
            <div className="uul-cost-col">
              <span className="uul-total-cost">
                {centsToDollars(totals?.totalCostCents)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

UsageUserList.propTypes = {
  userSummaries: PropTypes.arrayOf(
    PropTypes.shape({
      userId:      PropTypes.number.isRequired,
      email:       PropTypes.string,
      displayName: PropTypes.string,
      services:    PropTypes.object,
      totals:      PropTypes.shape({
        totalCostCents: PropTypes.number,
        totalCallCount: PropTypes.number,
      }),
    })
  ),
};

export default UsageUserList;